import { useEffect, useRef, useState } from 'react';

import { SCADA_ITEMS_MAX } from '@/domain/providerConfig';

import {
    parseScadaRows,
    type ScadaRow,
    type ScadaRowProblems,
    scadaRowProblems,
    serializeScadaRows,
} from './scadaRows';

const EMPTY_ROW: ScadaRow = { itemId: '', signal: '', feedLine: '' };

/**
 * Whether focus leaving part of a row means the user left the row. Focus moving within the
 * row is still the row being filled in, and so is switching to another window, which is how
 * an item id is usually copied from Clarify: that blur has no target and the document has
 * lost focus too.
 */
export function leavesRow(row: Element, next: Element | null, documentHasFocus: boolean): boolean {
    if (next === null) return documentHasFocus;
    return !row.contains(next);
}

/**
 * Keys that follow a row through adds and removes, so what a row holds of its own (a
 * picker's search) stays with it. Entries carry no id, and the index moves on a remove.
 */
function useRowKeys(count: number): {
    keys: string[];
    add: () => string;
    removeAt: (index: number) => void;
} {
    const next = useRef(0);
    const make = (): string => {
        next.current += 1;
        return `row${next.current}`;
    };
    const [keys, setKeys] = useState<string[]>(() => Array.from({ length: count }, make));
    let current = keys;
    // A list replaced from outside, by a reset or a merge, starts with fresh keys.
    if (keys.length !== count) {
        current = Array.from({ length: count }, make);
        setKeys(current);
    }
    return {
        keys: current,
        add: () => {
            const key = make();
            setKeys([...current, key]);
            return key;
        },
        removeAt: (index) => setKeys(current.filter((_, at) => at !== index)),
    };
}

export interface ScadaRowsArgs {
    id: string;
    value: string;
    onChange: (text: string) => void;
    signals: readonly string[];
    error: string | null;
}

export interface ScadaRows {
    rows: ScadaRow[];
    keys: string[];
    isFull: boolean;
    faultAt: (index: number) => ScadaRowProblems | undefined;
    /** Whether the list's message shows. */
    showError: boolean;
    /** The list has an error that only rows not yet filled in or left break. */
    heldBack: boolean;
    update: (index: number, change: Partial<ScadaRow>) => void;
    remove: (index: number) => void;
    addRow: () => void;
    leave: (index: number) => void;
}

/** The rows of a SCADA list and which of them may be marked yet. */
export function useScadaRows({ id, value, onChange, signals, error }: ScadaRowsArgs): ScadaRows {
    const rows = parseScadaRows(value);
    const { keys, add, removeAt } = useRowKeys(rows.length);
    // Rows added here that the user has not typed in or left yet: a blank row is not a
    // mistake until then. Stored rows are never fresh, so a bad stored entry shows at once.
    const [fresh, setFresh] = useState<ReadonlySet<string>>(() => new Set());
    const focusKey = useRef<string | null>(null);
    const isFresh = (index: number): boolean => fresh.has(keys[index]);
    const problems = error === null ? null : scadaRowProblems(rows, signals);
    const rowFaults = rows.map((_, index) => {
        const fault = problems?.[index];
        return fault !== undefined && (fault.itemId || fault.signal || fault.feedLine);
    });
    // A rule the rows do not explain (the list as a whole) shows at once; one that only a
    // fresh row breaks waits for that row.
    const showError =
        error !== null &&
        (!rowFaults.some(Boolean) || rowFaults.some((fault, index) => fault && !isFresh(index)));

    useEffect(() => {
        if (focusKey.current === null) return;
        document.getElementById(`${id}-${focusKey.current}-item`)?.focus();
        focusKey.current = null;
    });

    function leave(index: number): void {
        const key = keys[index];
        if (!fresh.has(key)) return;
        const next = new Set(fresh);
        next.delete(key);
        setFresh(next);
    }

    return {
        rows,
        keys,
        isFull: rows.length >= SCADA_ITEMS_MAX,
        faultAt: (index) => (isFresh(index) ? undefined : problems?.[index]),
        showError,
        heldBack: error !== null && !showError,
        update: (index, change) => {
            leave(index);
            onChange(
                serializeScadaRows(
                    rows.map((row, at) => (at === index ? { ...row, ...change } : row))
                )
            );
        },
        remove: (index) => {
            removeAt(index);
            onChange(serializeScadaRows(rows.filter((_, at) => at !== index)));
        },
        addRow: () => {
            const key = add();
            setFresh(new Set([...fresh, key]));
            // Into the new row, so leaving it is what marks it.
            focusKey.current = key;
            onChange(serializeScadaRows([...rows, EMPTY_ROW]));
        },
        leave,
    };
}
